/**
 * A short, plain-English description of a class, for a snapshot (D-380).
 *
 * **Not `classLabel`, deliberately.** That answers *"what is this class
 * called"* and returns the whole Jackrabbit name, which is right on a register
 * heading where a teacher has to be certain which of two sections she is
 * marking. This answers *"what kind of class is this"* in a few words, for a
 * row on Home that has to fit on a phone. Two questions, two functions —
 * merging them would make one screen wrong.
 *
 * **Order: the routine name, then a derived word, then nothing.** The studio's
 * own `routineName` always wins. Everything else is inference from a machine
 * name and is offered alongside the real name, never instead of it.
 */

/** What the row says, and the smaller line under it. */
export type ClassDescription = {
  /** A few words: "Ballet", "Hip hop trio", "Tippy Toes". */
  label: string
  /** Extra disciplines, when the label is a name rather than a style. */
  detail: string | null
  /** True when this came from the studio rather than from parsing. */
  isTheirs: boolean
  /**
   * Who a solo, duet or trio is for, when the name says.
   *
   * **The teaching row is unreadable without it.** Ashley teaches three solos
   * on a Tuesday and they render as three rows saying "Solo", distinguished
   * only by a time. The names are in the second segment and were discarded
   * with the rest of it. A dancer own row does not want this — that row is
   * already headed with her name.
   */
  who: string | null
}

/*
 * Longest and most specific first. `TECH_BALLET` is ballet rather than
 * technique, and "musical theater" must beat a bare "MT".
 */
const STYLES: [RegExp, string][] = [
  [/MUSICAL THEAT(ER|RE)|\bMUS THEATER\b|\bMT\b/, 'Musical theatre'],
  [/HIP ?HOP/, 'Hip hop'],
  [/CONTEMP\w*/, 'Contemporary'],
  [/\bLYRICAL\b|\bLYR\b/, 'Lyrical'],
  [/\bBALLROOM\b/, 'Ballroom'],
  [/\bPOINTE\b/, 'Pointe'],
  [/\bBALLET\b/, 'Ballet'],
  [/\bJAZZ\b/, 'Jazz'],
  [/\bTAP\b/, 'Tap'],
  [/\bACRO\b/, 'Acro'],
  /*
   * Her word, not a synonym for it. The studio writes "Tumbling" in fifteen
   * class names and "Acro" in others; folding one into the other is a screen
   * deciding what she calls her own discipline, and these twenty entries are
   * the closest thing here to her vocabulary.
   */
  [/\bTUMBLING\b|\bTUMBL\w*/, 'Tumbling'],
  [/\bPOM\b/, 'Pom'],
  [/\bSDT\b/, 'Song and dance'],
  [/\bCLOG\w*/, 'Clogging'],
  [/\bPRODUCTION\b/, 'Production'],
  [/\bCONDITIONING\b/, 'Conditioning'],
  [/\bCOMBO\b/, 'Combo'],
  [/\bOPEN\b/, 'Open'],
  [/\bPBT\b/, 'Progressing ballet technique'],
  [/\bLUIGI\b/, 'Luigi technique'],
  [/\bTECH\b|\bTECHNIQUE\b|\bSKILLS\b/, 'Technique'],
]

/** How many dancers, which the name states and the style does not. */
const SHAPES: [RegExp, string][] = [
  [/\bSOLO\b/, 'solo'],
  [/\bDUET\b|\bDUO\b/, 'duet'],
  [/\bTRIO\b/, 'trio'],
]

/**
 * Separators to spaces, so a word boundary can find the words.
 *
 * **This is the whole reason the first version of this failed.** `\b` treats an
 * underscore as a word character, so `\bJAZZ\b` never matched
 * `COMP_TECH_JAZZ_1` — and underscores are how every competitive class here is
 * written. It labelled 13% of the studio before this line existed.
 */
const separate = (text: string) =>
  text.replace(/[_|/,+.\-*]/g, ' ').replace(/\s+/g, ' ').trim()

const shout = (text: string) => separate(text).toUpperCase()

const titled = (text: string) =>
  separate(text)
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())

const stylesIn = (text: string) =>
  STYLES.filter(([pattern]) => pattern.test(text)).map(([, label]) => label)

/**
 * The dancers a solo, duet or trio is named for.
 *
 * **Verbatim, not parsed into people.** `CHARKATE_PARKER_GREER` is plainly
 * three and `EMMA LAINEY` is plainly two, but `NANCY (SKYE)` is one dancer and
 * a hint about who takes her — and there is no rule separating a two-word name
 * from two one-word names. Rendering what the studio wrote is right where
 * guessing is not.
 *
 * A parenthetical is dropped: it names the teacher or the parent, never the
 * dancer, and the row already says who is taking the class.
 */
function dancersIn(segment: string | undefined): string | null {
  if (!segment) return null

  const cleaned = separate(segment.replace(/\([^)]*\)/g, '')).trim()
  if (cleaned.length === 0) return null

  /*
   * A day or a time here means the name was left out, not that the time is the
   * name — `COMP_SOLO | TH_815 | MADDIE` is a slot with no dancer written on
   * it. Bare digits are included because the studio writes times as `815` and
   * `730` as often as `4:15`, and widening the shared day/time pattern to take
   * three and four digits would let it eat level numbers elsewhere.
   */
  const notAName = (word: string) => WHEN.test(word.toUpperCase()) || /^\d+$/.test(word)
  if (cleaned.split(' ').every(notAName)) return null

  return titled(cleaned)
}

/*
 * Words that say which programme or level a class is, rather than what it is.
 * Removing them is how "COMP_TECH_BALLET_3.5" reduces to nothing but the style.
 */
const NOISE = /^(COMP|REC|TECH|TECHNIQUE|SKILLS|MINI|MINIS|NEW|PRETEAM|A|B)$/

/*
 * A day or a time that has leaked into the head because the studio wrote
 * `SECONDARY Hiphop- Th 6:30` with no pipe before it. Without this the label
 * on a Home row reads "Secondary Hiphop Th 6:30", which is the machine name
 * coming back through the door this function exists to close.
 */
const WHEN =
  /^(\d{1,2}(:\d{2})?(A|P|AM|PM)?|MO|MON|TU|TUE|TUES|WE|WED|TH|THU|THUR|THURS|FR|FRI|SA|SAT|SU|SUN)$/

/**
 * Whether the head *is* a style, rather than merely containing one.
 *
 * **This is the Tippy Toes bug in its second disguise, and it stayed live
 * through the first fix.** `HIPHOP FLIPFLOP` is a name the studio says out loud
 * — a tumbling class for four-year-olds — but it happens to contain a style
 * word, so the head branch claimed it and returned "Hip hop": the same string
 * as `COMP_HIPHOP | TU 545 | 13/14`, a competitive line for teenagers, on the
 * same screen. The measurement over all 186 names could not see it, because it
 * was looking for labels that were *long* and this one is short.
 *
 * So: strip the styles and the programme words, and see whether anything is
 * left. `COMP_HIPHOP` leaves nothing and is a style. `HIPHOP FLIPFLOP` leaves
 * `FLIPFLOP` and is a name.
 */
function headIsAStyle(head: string, styles: string[]): boolean {
  if (styles.length === 0) return false

  const residue = shout(head)
    .split(' ')
    .filter((word) => {
      if (NOISE.test(word)) return false
      // A level or a size: 3.5, 2NEW, 6&UNDER.
      if (/\d/.test(word)) return false
      return !STYLES.some(([pattern]) => pattern.test(word))
    })

  return residue.length === 0
}

export function describeClass(
  className: string,
  routineName?: string | null,
): ClassDescription {
  const theirs = routineName?.trim()
  if (theirs) return { label: theirs, detail: null, isTheirs: true, who: null }

  /*
   * <b>A name a person typed is left exactly as they typed it</b> (D-581).
   *
   * Everything below exists to make sense of <i>Jackrabbit's</i> shape -
   * pipe-separated segments, SCREAMING_SNAKE tokens, a teacher's initials on
   * the end. Handed something written by hand it does real damage, and the
   * studio's own calendar is full of those: a class the app made (D-207) is
   * called <i>Tap makeup</i> or <i>ballet 4 makeup</i>, and this turned the
   * second into <b>Ballet Makeup</b> - title-cased, and <b>the level silently
   * dropped</b>.
   *
   * <b>Found when the calendar started reading names</b> (D-579) and three
   * portal tests went red; it had been true of the Today panel since D-190 and
   * nobody had looked, because those classes are rare and nobody reads a
   * makeup's name twice.
   *
   * <b>The test is the shape, not the origin.</b> Origin would be better -
   * D-263 keys on <c>ExternalSource</c> for exactly this reason - but the
   * calendar payload does not carry it, and a rule that reads the string is
   * available to every caller today rather than after a server change. A
   * Jackrabbit name always has a <c>|</c> or a <c>_</c> in it; a human name
   * almost never does.
   */
  const raw = className.trim()
  if (raw.length > 0 && !raw.includes('|') && !raw.includes('_')) {
    return { label: raw, detail: null, isTheirs: false, who: null }
  }

  const segments = className.split('|').map((s) => s.trim()).filter(Boolean)

  /*
   * `COMP|REC_TECH_ACRO` uses the pipe to mean *or*, not as a separator, so the
   * first segment is the bare word COMP and six real classes came out labelled
   * "Comp". A lone programme marker is glued to whatever follows it.
   */
  if (/^(COMP|REC)$/i.test(segments[0] ?? '') && segments.length > 1) {
    segments.splice(0, 2, `${segments[0]} ${segments[1]}`)
  }

  const head = segments[0] ?? className

  /*
   * The last segment is the teacher. Dropped before any style is looked for,
   * so a colleague can never rename a class — none of them is called Jazz
   * today, and that is luck rather than a rule.
   */
  const rest = segments.slice(1, Math.max(1, segments.length - 1)).join(' ')

  const headStyles = stylesIn(shout(head))
  const shape = SHAPES.find(([pattern]) => pattern.test(shout(head)))?.[1] ?? null

  if (shape) {
    /*
     * A solo carries no style in its name at all. A trio sometimes hides one
     * in the time segment — `TUE_730_BIWKLY_HIPHOP` — and that is worth having.
     */
    const style = headStyles[0] ?? stylesIn(shout(rest))[0] ?? null

    return {
      label: style ? `${style} ${shape}` : shape[0].toUpperCase() + shape.slice(1),
      detail: null,
      isTheirs: false,
      who: dancersIn(segments[1]),
    }
  }

  if (headIsAStyle(head, headStyles)) {
    return { label: headStyles[0], detail: null, isTheirs: false, who: null }
  }

  /*
   * Nothing in the head, so the head is a name the studio says out loud —
   * Tippy Toes, Happy Feet, Lil Flipz — and whatever the later segments list
   * becomes the detail rather than the label.
   *
   * **This is the second bug the measurement caught.** Searching every segment
   * turned all eleven Tippy Toes and Tiny Tutus classes into "Ballet", the
   * first discipline in a list of three. A parent looking for Tippy Toes would
   * not have found it, and a class that teaches three things is not one of
   * them.
   */
  const detail = stylesIn(shout(rest)).map((s) => s.toLowerCase())

  // The day and time drop out, so a head the studio wrote without a separator
  // still reads as a name rather than as half a schedule.
  const name = separate(head)
    .split(' ')
    .filter((word) => !WHEN.test(word.toUpperCase()))
    .join(' ')

  return {
    label: titled(name) || titled(head) || className,
    detail: detail.length > 0 ? detail.join(', ') : null,
    isTheirs: false,
    // Only a solo, duet or trio names its dancers; a group class names a band.
    who: null,
  }
}
